import {Observable, Subject} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {Mp4} from './mp4';
import {stringifyError} from '../../ui/console/stringify-error';

declare const Hls: any;

export class Hlsjs extends Mp4 {
  isLive = true;
  isSeekable = false;

  stalled$ = new Subject();

  protected hls: any;
  private _src: string;

  private files = {};

  constructor() {
    super();
  }

  get source() {
    return this._src;
  }

  private get bufferLength(): number {
    return this.video.buffered.end(this.video.buffered.length - 1) - this.video.buffered.start(0);
  }

  play() {
    // this.reattachMedia();
    if (this.hls) {
      this.hls.startLoad();
    }
    return super.play();
  }

  stop() {
    if (this.hls) {
      this.hls.stopLoad();
    }

    super.stop();
  }

  attachMedia(video: HTMLVideoElement) {
    super.attachMedia(video);
  }

  reattachMedia() {
    this.hls.detachMedia();
    this.hls.attachMedia(this.video);
  }

  loadSource(src: string) {
    // console.log('loadSource', src);

    this._src = src;

    if (this.hls) {
      this.hls.destroy();
    }

    this.createHls();
    this.hls.loadSource(src);
  }

  protected loadingProgress({loaded, total}) {
    const loadingProgress = parseInt((100 * loaded / total).toFixed(0));

    console.log(loadingProgress, {loaded, total});

    if (this.buffer() <= 0.2 || loadingProgress > 99) {
      this.loading$.next(loadingProgress);
    }
  }

  protected on<T>(eventName: string) {
    return new Observable<T>(observer => {
      this.hls.on(eventName as any, (event: any, data: T) => observer.next(data));
    });
  }

  protected setupWatchDog() {
  }

  protected reportSpeed(speed: number) {
    const q = this.video.getVideoPlaybackQuality();

    this.stats$.next({
      currentLevel: this.hls.nextLoadLevel,
      speed: Math.round(speed),
      bandwidthEstimate: Math.round(this.hls.bandwidthEstimate / 1024),
      targetLatency: this.hls.targetLatency,
      latency: this.hls.latency.toFixed(3),
      buffer: this.buffer(),
      droppedFrames: q.droppedVideoFrames + ' ' + Math.round(100 * (q.droppedVideoFrames / q.totalVideoFrames)) + '%'
    });
  }

  protected logError(e) {
    this.log$.next(stringifyError(e));
    console.error('logError', e);
  }

  protected retryAfterDelay() {

  }

  private createHls() {
    let config: any = {
      debug: false,
      enableWorker: true,
      lowLatencyMode: false,
      backBufferLength: 90,
    };

    console.log({isLive: this.isLive});

    if (this.isLive) {
      const debug = window.localStorage.getItem('gg-player-debug') === 'true';

      const externalConfig = 'ggPlayerConfig' in window ? window['ggPlayerConfig'] : {};

      config = {
        ...config,
        debug: debug,
        live: true,
        autoStartLoad: false,
        enableWorker: false,
        lowLatencyMode: true,
        maxBufferHole: 3,

        testBandwidth: false,
        abrEwmaDefaultEstimate: 30000000,

        abrEwmaFastLive: 2,
        abrEwmaSlowLive: 4,

        backBufferLength: 90,
        liveSyncDurationCount: 1.35,
        maxLiveSyncPlaybackRate: 1.1,

        levelLoadingTimeout: 680,
        levelLoadingRetryDelay: 10,
        levelLoadingMaxRetryTimeout: 5000,
        levelLoadingMaxRetry: 5 * 60 * 60,

        fragLoadingTimeout: 550,
        fragLoadingRetryDelay: 10,
        fragLoadingMaxRetryTimeout: 5000,
        fragLoadingMaxRetry: 5 * 60 * 60,

        // liveSyncDuration: 2,
        // maxLiveSyncPlaybackRate: 1.5,

        ...externalConfig
      };
    }

    this.log$.next('Hls config');
    this.log$.next(JSON.stringify(config, null, 1));

    this.hls = new Hls(config);

    this.hls.attachMedia(this.video);
    this.setupWatchDog();
    this.subscribeEvents();

  }

  private measureDownloadSpeed(part, size?) {
    const file = part.url;

    if (size) {
      if (!this.files[file]) {
        return;
      }

      if (size < 5000) {
        return;
      }

      const time = Date.now() - this.files[file];
      delete this.files[file];
      const speed = 8 * size / time;

      return this.reportSpeed(speed);
    }

    this.files[file] = Date.now();
  }

  private subscribeEvents() {
    this.on(Hls.Events.FRAG_LOADING).pipe(filter((data: any) => data.part)).subscribe((data: any) => this.measureDownloadSpeed(data.part));
    this.on(Hls.Events.FRAG_LOADED).pipe(filter((data: any) => data.part)).subscribe((data: any) => this.measureDownloadSpeed(data.part, data.part.stats.total));

    const errors$ = this.on(Hls.Events.ERROR).pipe(tap(e => this.logError(e)));

    errors$.pipe(
      filter((e: any) => e.details === 'bufferStalledError'),
      filter(_ => this.video.currentTime > 30),
      tap(_ => console.log('Stalled caught'))
    ).subscribe(e => this.stalled$.next(e.buffer));

    errors$.pipe(
      filter((e: any) => e.details === 'manifestLoadError'),
    ).subscribe(e => this.retryAfterDelay());

    // errors$.pipe(
    //   filter((e: any) => e.details === 'bufferNudgeOnStall'),
    // ).subscribe(e => this.reattachMedia());


    // this.on(Hls.Events).subscribe((e) => this.logError(e));
    // this.video.addEventListener()

    // for (const eventName of Object.keys(Hls.Events)) {
    //   this.hls.on(Hls.Events[eventName], (...args) => console.log(args));
    // }
  }
}
